import type { ResumoDashboard } from "@base/contracts";
import { BarChart3, TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { EstadoVazio } from "@base/ui";
import { Badge } from "@/components/ui/badge";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DIAS_SEMANA,
  formatarHorario,
  formatarNumero,
  formatarPercentual,
  ROTULOS_FORMATO,
} from "./dashboard-formatadores";
import {
  ChipStatus,
  MetricaCompacta,
  NumeroHero,
  Painel,
  PainelCabecalho,
  PainelCorpo,
} from "./dashboard-ui";

const chartSerieConfig = {
  alcance: { label: "Alcance", color: "var(--chart-1)" },
  engajamento: { label: "Engajamento", color: "var(--chart-2)" },
} satisfies ChartConfig;

const chartFormatoConfig = {
  alcance: { label: "Alcance médio", color: "var(--chart-1)" },
} satisfies ChartConfig;

const chartComparativoConfig = {
  atual: { label: "Este período", color: "var(--chart-1)" },
  anterior: { label: "Período anterior", color: "var(--chart-2)" },
} satisfies ChartConfig;

export function AbaPerformance({
  resumo,
}: {
  readonly resumo: ResumoDashboard;
}) {
  const perf = resumo.performance;

  if (!perf.temMetricas && !perf.metaConectada) {
    return (
      <EstadoVazio
        icone={<BarChart3 className="size-8" aria-hidden="true" />}
        titulo="Conecte Facebook e Instagram do cliente"
        descricao="Vincule a página e o Instagram em Redes do cliente, nas configurações do projeto, para ver os números reais."
      />
    );
  }

  if (!perf.temMetricas) {
    return (
      <div className="grid gap-6 md:grid-cols-12">
        <Painel className="dashboard-hero md:col-span-7">
          <PainelCorpo className="relative z-[1] space-y-4 py-10 sm:py-14">
            <ChipStatus tom="marca">Insights</ChipStatus>
            <h2 className="max-w-md text-2xl font-semibold tracking-[-0.03em] text-foreground sm:text-3xl">
              Aguardando a primeira coleta
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
              Publique no Instagram e aguarde a coleta do dia, ou dispare uma
              coleta manual no topo.
            </p>
          </PainelCorpo>
        </Painel>
        <Painel className="md:col-span-5">
          <PainelCorpo className="flex h-full flex-col justify-center gap-4 py-10">
            <TrendingUp
              className="size-8 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="text-sm leading-relaxed text-muted-foreground">
              Quando a coleta rodar, aqui entram alcance, engajamento, formatos
              e o heatmap de horário.
            </p>
          </PainelCorpo>
        </Painel>
      </div>
    );
  }

  const porFormato = Object.entries(
    perf.topPosts.reduce<Record<string, { total: number; qtd: number }>>(
      (acc, post) => {
        const atual = acc[post.formato] ?? { total: 0, qtd: 0 };
        acc[post.formato] = {
          total: atual.total + post.alcance,
          qtd: atual.qtd + 1,
        };
        return acc;
      },
      {},
    ),
  ).map(([formato, dados]) => ({
    formato: ROTULOS_FORMATO[formato] ?? formato,
    alcance: Math.round(dados.total / dados.qtd),
  }));

  const maxHeat = Math.max(
    1,
    ...perf.heatmapHorario.map((s) => s.engajamento),
  );

  const crescimento =
    perf.crescimentoSeguidores === null
      ? "Sem dados"
      : `${perf.crescimentoSeguidores > 0 ? "+" : ""}${formatarNumero(perf.crescimentoSeguidores)}`;

  return (
    <div className="flex flex-col gap-8">
      <Painel className="dashboard-hero" aria-label="Métricas de engajamento">
        <div className="relative z-[1] grid gap-0 md:grid-cols-12">
          <div className="flex flex-col justify-between gap-6 border-b border-border/60 p-6 sm:p-8 md:col-span-5 md:border-b-0 md:border-r">
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">
                Taxa de engajamento
              </p>
              <NumeroHero tom="marca">
                {formatarPercentual(perf.taxaEngajamento)}
              </NumeroHero>
              <p className="text-sm text-muted-foreground">
                Melhor formato:{" "}
                <span className="font-medium text-foreground">
                  {perf.melhorFormato
                    ? (ROTULOS_FORMATO[perf.melhorFormato] ?? perf.melhorFormato)
                    : "Sem dados"}
                </span>
                {" · "}
                horário {formatarHorario(perf.melhorHorario)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 divide-y divide-border/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:col-span-7">
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Salvamentos"
                valor={formatarNumero(perf.totalSalvamentos)}
              />
            </div>
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Compartilhamentos"
                valor={formatarNumero(perf.totalCompartilhamentos)}
              />
            </div>
            <div className="px-6 py-5 md:py-8">
              <MetricaCompacta
                rotulo="Seguidores"
                valor={crescimento}
                detalhe="Variação no período"
              />
            </div>
          </div>
        </div>
      </Painel>

      {perf.comparativoPeriodo ? (
        <Painel>
          <PainelCabecalho
            titulo="Comparativo com o período anterior"
            descricao="Mesmo tamanho de janela, lado a lado."
          />
          <PainelCorpo>
            <ChartContainer
              config={chartComparativoConfig}
              className="aspect-[21/9] w-full max-h-[260px]"
            >
              <BarChart
                data={[
                  {
                    metrica: "Alcance",
                    atual: perf.comparativoPeriodo.alcance.atual,
                    anterior: perf.comparativoPeriodo.alcance.anterior,
                  },
                  {
                    metrica: "Engajamento",
                    atual: perf.comparativoPeriodo.engajamento.atual,
                    anterior: perf.comparativoPeriodo.engajamento.anterior,
                  },
                  {
                    metrica: "Publicados",
                    atual: perf.comparativoPeriodo.publicados.atual,
                    anterior: perf.comparativoPeriodo.publicados.anterior,
                  },
                ]}
                margin={{ left: 4, right: 8, top: 8 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="metrica"
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={40}
                  fontSize={11}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar
                  dataKey="atual"
                  fill="var(--color-atual)"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="anterior"
                  fill="var(--color-anterior)"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </PainelCorpo>
        </Painel>
      ) : null}

      <Painel>
        <PainelCabecalho
          titulo="Tendência no período"
          descricao="Alcance e engajamento por dia."
        />
        <PainelCorpo>
          <ChartContainer
            config={chartSerieConfig}
            className="aspect-[21/9] w-full max-h-[300px]"
          >
            <AreaChart
              data={perf.serieDiaria}
              margin={{ left: 4, right: 8, top: 8, bottom: 0 }}
            >
              <defs>
                <linearGradient id="fillAlcanceDash" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--color-alcance)"
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-alcance)"
                    stopOpacity={0.02}
                  />
                </linearGradient>
                <linearGradient
                  id="fillEngajamentoDash"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor="var(--color-engajamento)"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--color-engajamento)"
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="data"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                fontSize={10}
                tickFormatter={(v: string) => v.slice(5)}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={40}
                fontSize={11}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                type="monotone"
                dataKey="alcance"
                stroke="var(--color-alcance)"
                fill="url(#fillAlcanceDash)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="engajamento"
                stroke="var(--color-engajamento)"
                fill="url(#fillEngajamentoDash)"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        </PainelCorpo>
      </Painel>

      <section
        className="grid gap-6 lg:grid-cols-12"
        aria-label="Formatos e horários"
      >
        <Painel className="lg:col-span-5">
          <PainelCabecalho
            titulo="Por formato"
            descricao="Alcance médio dos top conteúdos."
          />
          <PainelCorpo>
            {porFormato.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem breakdown.</p>
            ) : (
              <ChartContainer
                config={chartFormatoConfig}
                className="aspect-[16/10] w-full max-h-[240px]"
              >
                <BarChart data={porFormato} margin={{ left: 4, right: 8 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="formato"
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    fontSize={11}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="alcance"
                    fill="var(--color-alcance)"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            )}
          </PainelCorpo>
        </Painel>

        <Painel className="lg:col-span-7">
          <PainelCabecalho
            titulo="Heatmap de horário"
            descricao="Dia da semana × hora (UTC) por engajamento."
          />
          <PainelCorpo>
            {perf.heatmapHorario.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sem slots suficientes ainda.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <div
                  className="grid min-w-[28rem] gap-1"
                  style={{
                    gridTemplateColumns: `3rem repeat(24, minmax(0, 1fr))`,
                  }}
                >
                  <div />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={h}
                      className="text-center text-[9px] text-muted-foreground"
                    >
                      {h % 3 === 0 ? h : ""}
                    </div>
                  ))}
                  {DIAS_SEMANA.map((dia, diaIdx) => (
                    <HeatmapLinha
                      key={dia}
                      rotulo={dia}
                      diaSemana={diaIdx}
                      slots={perf.heatmapHorario}
                      max={maxHeat}
                    />
                  ))}
                </div>
              </div>
            )}
          </PainelCorpo>
        </Painel>
      </section>

      <Painel>
        <PainelCabecalho
          titulo="Top conteúdos"
          descricao="Os cinco com maior alcance no recorte."
        />
        <PainelCorpo semPadding>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Post</TableHead>
                <TableHead>Formato</TableHead>
                <TableHead className="text-right">Alcance</TableHead>
                <TableHead className="text-right">ER%</TableHead>
                <TableHead className="pr-6 text-right">Engajamento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perf.topPosts.map((post, indice) => (
                <TableRow key={post.conteudoId}>
                  <TableCell className="max-w-[16rem] pl-6">
                    <div className="flex items-center gap-3">
                      <span className="w-5 text-xs tabular-nums text-muted-foreground">
                        {indice + 1}
                      </span>
                      <span className="truncate font-medium">{post.titulo}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal">
                      {ROTULOS_FORMATO[post.formato] ?? post.formato}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatarNumero(post.alcance)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatarPercentual(post.taxaEngajamento)}
                  </TableCell>
                  <TableCell className="pr-6 text-right tabular-nums">
                    {formatarNumero(post.engajamento)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PainelCorpo>
      </Painel>
    </div>
  );
}

function HeatmapLinha({
  rotulo,
  diaSemana,
  slots,
  max,
}: {
  readonly rotulo: string;
  readonly diaSemana: number;
  readonly slots: ResumoDashboard["performance"]["heatmapHorario"];
  readonly max: number;
}) {
  return (
    <>
      <div className="flex items-center text-xs text-muted-foreground">
        {rotulo}
      </div>
      {Array.from({ length: 24 }, (_, hora) => {
        const slot = slots.find(
          (s) => s.diaSemana === diaSemana && s.hora === hora,
        );
        const intensidade = slot ? slot.engajamento / max : 0;
        return (
          <div
            key={hora}
            title={
              slot
                ? `${rotulo} ${hora}h: ${slot.engajamento} eng. (${slot.publicacoes} posts)`
                : undefined
            }
            className="aspect-square rounded-[3px] border border-border/30"
            style={{
              backgroundColor:
                intensidade > 0
                  ? `color-mix(in srgb, var(--rk-text-brand) ${Math.max(18, Math.round(intensidade * 100))}%, var(--muted))`
                  : "var(--muted)",
            }}
          />
        );
      })}
    </>
  );
}
